import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { loadBranches } from "@/lib/branches";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("ntc_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); loadBranches(); })
      .catch(() => {
        localStorage.removeItem("ntc_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const persist = (data) => {
    localStorage.setItem("ntc_token", data.token);
    setUser(data.user);
    loadBranches();
  };

  const login = async (payload) => {
    const res = await api.post("/auth/login", payload);
    if (res.data?.token) persist(res.data);
    return res.data;
  };

  const verifyLoginOtp = async (identifier, code) => {
    const res = await api.post("/auth/login/verify-otp", { ...identifier, code });
    persist(res.data);
  };

  const requestOtp = async (phone) => {
    const res = await api.post("/auth/request-otp", { phone });
    return res.data; // { demo_otp }
  };

  const verifyOtp = async (phone, code) => {
    const res = await api.post("/auth/verify-otp", { phone, code });
    persist(res.data);
  };

  const forgotRequestOtp = async (identifier) => {
    const res = await api.post("/auth/forgot-password/request-otp", identifier);
    return res.data; // { demo_otp?, email, message }
  };

  const resetPassword = async (identifier, code, new_password) => {
    const res = await api.post("/auth/reset-password", { ...identifier, code, new_password });
    return res.data;
  };

  const changePassword = async (current_password, new_password) => {
    const res = await api.post("/auth/change-password", { current_password, new_password });
    return res.data;
  };

  const updatePhone = async (phone) => {
    const res = await api.put("/auth/phone", { phone });
    setUser((u) => (u ? { ...u, phone: res.data.phone } : u));
    return res.data;
  };

  const updateEmail = async (email, otp_email) => {
    const res = await api.put("/auth/email", { email, otp_email });
    setUser((u) => (u ? { ...u, email: res.data.email, otp_email: res.data.otp_email } : u));
    return res.data;
  };

  const updateBranch = async (office) => {
    const res = await api.put("/auth/branch", { office });
    setUser((u) => (u ? { ...u, office: res.data.office } : u));
    return res.data;
  };

  const register = async (name, email, password, phone, office, user_id) => {
    const res = await api.post("/auth/register", { name, email, password, phone, office, user_id });
    persist(res.data);
  };

  const logout = () => {
    localStorage.removeItem("ntc_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        login,
        verifyLoginOtp,
        register,
        logout,
        requestOtp,
        verifyOtp,
        forgotRequestOtp,
        resetPassword,
        changePassword,
        updatePhone,
        updateEmail,
        updateBranch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
